import {
  getProductKeys,
  selectionsUrlOverride,
  isEditDesignInCartMode
} from '~/utils/url';
import {getProductIdViewIdKey} from '~/utils/product';
import {getNormalizedCategoryPath} from '~/utils/catalog';
import {List, Map} from 'immutable';
import Product from '~/data/records/Product';
// types
import DesignTypes from '~/constants/types/DesignTypes';
import FundraisingTypes from '~/constants/types/FundraisingTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import QuoteTypes from '~/constants/types/QuoteTypes';
// constants
import {
  DEFAULT_STYLE_ID,
  DEFAULT_COLOR_ID,
  DEFAULT_CATEGORY_PATH
} from '~/constants/ProductConstants';
import {makeUndoable} from '~/utils/undoable';

export function initialState(
  defaultStyleId = DEFAULT_STYLE_ID,
  defaultColorId = DEFAULT_COLOR_ID,
  defaultCategoryPath = DEFAULT_CATEGORY_PATH
) {
  const productKeys = isEditDesignInCartMode() ? {} : getProductKeys();
  const styleId = productKeys.styleId || defaultStyleId;
  const colorId = productKeys.colorId || defaultColorId;
  const product = new Product(
    styleId && colorId ? {styleId, colorId, visible: true} : undefined
  );
  const selections = List([product]);

  return Map({
    anchorStyleId: product.styleId,
    categoryPaths: Map({
      [styleId]: defaultCategoryPath
    }),
    currentColorId: product.colorId,
    lastSavedSelections: selections.hashCode(),
    selections,
    hasInvalidProductImage: {},
    swapProductInProgress: false
  });
}

function isSelectionValid(state, colorId) {
  return state
    .get('selections')
    .some(selection => colorId === selection.get('colorId'));
}

function getCurrentSelectedIndex(state, previousColorId) {
  return state
    .get('selections')
    .findIndex(product => previousColorId === product.colorId);
}

export function productReducer(state = initialState(), action) {
  switch (action.type) {
    case DesignTypes.SAVE_SUCCESS:
    case FundraisingTypes.SAVE_SUCCESS: {
      return state.set(
        'lastSavedSelections',
        state.get('selections').hashCode()
      );
    }

    case ProductTypes.ADD_PRODUCT: {
      const {products, isMerging} = action.payload;
      if (isMerging) {
        return state.update('selections', list => {
          return list.unshift(...products.map(p => new Product(p)));
        });
      } else {
        return state
          .update('selections', list =>
            list.push(...products.map(p => new Product(p)))
          )
          .set('currentColorId', products[products.length - 1].colorId);
      }
    }

    case ProductTypes.CHANGE_COLOR: {
      const {previousColorId, nextColorId} = action.payload;
      const selectedIndex = getCurrentSelectedIndex(state, previousColorId);
      return state
        .setIn(['selections', selectedIndex, 'colorId'], nextColorId)
        .set('currentColorId', nextColorId);
    }

    case DesignTypes.SET_COMBINE_DESIGNS: {
      const {previousQuoteQuantities, combinedAnchorStyleId, isCombined} =
        action.payload;
      let newState = state;
      if (isCombined) {
        if (previousQuoteQuantities) {
          previousQuoteQuantities.forEach(previousProduct => {
            const selectedIndex = getCurrentSelectedIndex(
              state,
              previousProduct.colorId
            );
            newState = newState.setIn(
              ['selections', selectedIndex, 'visible'],
              false
            );
          });
        }
        return newState.set('anchorStyleId', combinedAnchorStyleId);
      } else {
        return state
          .update('selections', currentList => {
            return currentList.filter(
              currentProduct => currentProduct.visible === true
            );
          })
          .update(nextState =>
            nextState.update('anchorStyleId', () => {
              const nextSelections = nextState.get('selections');
              const nextStyleId = nextSelections.get(0).styleId;
              return nextStyleId;
            })
          );
      }
    }

    case ProductTypes.DELETE_PRODUCT: {
      const {colorId} = action.payload;
      if (state.get('selections').size <= 1) {
        throw new Error('Attempting to delete last remaining product');
      }

      return state
        .update('selections', list =>
          list.filter(product => colorId !== product.colorId)
        )
        .update(nextState =>
          nextState
            .update('currentColorId', activeColor => {
              const nextSelection = nextState.getIn([
                'selections',
                0,
                'colorId'
              ]);
              return activeColor === colorId ? nextSelection : activeColor;
            })
            .update('anchorStyleId', anchorStyle => {
              const selections = nextState.get('selections');
              if (!selections.find(s => s.styleId === anchorStyle)) {
                return selections.first()?.styleId;
              } else {
                return anchorStyle;
              }
            })
        );
    }

    case ProductTypes.REPLACE_PRODUCTS: {
      const {products} = action.payload;
      // eslint-disable-next-line no-unsafe-optional-chaining
      const {noUrlOverride} = action?.meta;
      const selections = noUrlOverride
        ? products
        : selectionsUrlOverride(products);
      const newSelections = List(selections.map(p => new Product(p)));
      return state.merge({
        selections: newSelections,
        currentColorId: newSelections.first().colorId,
        lastSavedSelections: newSelections.hashCode(),
        anchorStyleId: newSelections.first().styleId
      });
    }

    case ProductTypes.REPLACE_PRODUCTS_CART_MODE: {
      const {products} = action.payload;
      const selections = selectionsUrlOverride(products);
      const newSelections = List(selections.map(p => new Product(p)));
      return state.merge({
        selections: newSelections,
        currentColorId: newSelections.first().colorId,
        lastSavedSelections: null,
        anchorStyleId: newSelections.first().styleId
      });
    }

    case ProductTypes.RESET: {
      const {styleId, colorId, categoryPath} = action.payload;
      return initialState(styleId, colorId, categoryPath);
    }

    case ProductTypes.SELECT_PRODUCT: {
      const colorId = action.payload;
      if (isSelectionValid(state, colorId)) {
        return state.set('currentColorId', colorId);
      } else {
        throw new Error(`Tried to select invalid product ${colorId}`);
      }
    }

    case ProductTypes.SWAP_PRODUCT: {
      const {nextStyleId, previousColorId, nextColorIds, nextCategoryPath} =
        action.payload;
      const {clearPreviousProducts} = action.meta;
      const normalizedCategoryPath = getNormalizedCategoryPath({
        categoryPath: nextCategoryPath
      });
      const selectedIndex = getCurrentSelectedIndex(state, previousColorId);
      const styleToReplace = state.getIn([
        'selections',
        selectedIndex,
        'styleId'
      ]);

      if (clearPreviousProducts) {
        return state
          .update('selections', selections => {
            return selections
              .filter(
                s => s.styleId === styleToReplace && nextColorIds[s.colorId]
              )
              .map(
                s =>
                  new Product({
                    styleId: nextStyleId,
                    colorId: nextColorIds[s.colorId]
                  })
              );
          })
          .update('categoryPaths', paths =>
            paths.set(nextStyleId, normalizedCategoryPath)
          )
          .set('currentColorId', nextColorIds[previousColorId])
          .set('anchorStyleId', nextStyleId);
      }

      const newState = state
        .update('selections', selections => {
          return selections.map(s => {
            if (s.styleId === styleToReplace && nextColorIds[s.colorId]) {
              return new Product({
                styleId: nextStyleId,
                colorId: nextColorIds[s.colorId]
              });
            }
            return s;
          });
        })
        .update('categoryPaths', paths =>
          paths.set(nextStyleId, normalizedCategoryPath)
        )
        .set('currentColorId', nextColorIds[previousColorId]);

      if (selectedIndex === 0) {
        return newState.set('anchorStyleId', nextStyleId);
      }
      return newState;
    }

    case QuoteTypes.UPDATE_PRODUCT_QUANTITIES: {
      if (
        action.meta?.dontSortSelectionByQuantity ||
        state.get('selections').size === 1
      ) {
        return state;
      }

      const quantities = action.payload;

      if (!quantities) return state;

      // compile quantities by product
      const totalQuantities = Object.keys(quantities).reduce((acc, product) => {
        const quantity = quantities[product];
        if (!quantity) return acc;
        acc[product] = Object.keys(quantity).reduce(
          (total, size) => quantity[size] + total,
          0
        );
        return acc;
      }, {});

      return state
        .update('selections', list =>
          list.sort((productA, productB) => {
            return (
              totalQuantities[productB.colorId] -
              totalQuantities[productA.colorId]
            );
          })
        )
        .update(nextState =>
          nextState.update('anchorStyleId', anchorStyle => {
            // update anchor style if there are no quoted anchor style items
            const anchorColors = nextState
              .get('selections')
              .filter(s => s.styleId === anchorStyle)
              .map(s => s.colorId);
            const changeAnchor =
              anchorColors
                .toArray()
                .reduce((acc, color) => totalQuantities[color] + acc, 0) < 1;
            return changeAnchor
              ? nextState.get('selections').first().styleId
              : anchorStyle;
          })
        );
    }
    case ProductTypes.UPDATE_HAS_INVALID_PRODUCT_IMAGES: {
      const {productId, viewId} = action.payload;
      return state.set('hasInvalidProductImage', {
        ...state.get('hasInvalidProductImage'),
        [getProductIdViewIdKey({productId, viewId})]: true
      });
    }
    case ProductTypes.UPDATE_CATEGORY_PATHS: {
      const {categoryPath, styleId} = action.payload;
      return state.update('categoryPaths', paths =>
        paths.set(styleId, getNormalizedCategoryPath({categoryPath}))
      );
    }
    case ProductTypes.SET_ANCHOR_STYLE_AND_COLOR_ID: {
      const {styleId, colorId} = action.payload;
      return state
        .set('anchorStyleId', styleId)
        .set('currentColorId', colorId)
        .set(
          'selections',
          List([new Product({styleId, colorId, visible: true})])
        );
    }
    default:
      return state;
  }
}

export default makeUndoable(productReducer);
